import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap, flatMap, } from 'rxjs/operators';
import { LoadingController, ToastController } from '@ionic/angular';

declare var $: any;

@Injectable({
    providedIn: 'root'
  })
export class APIHelperService {

    public observable: Observable<any>;
    private loader: HTMLIonLoadingElement | null = null;
    private requestCount = 0; // number of active show requests

    constructor(
        private http: HttpClient,
        private toastController: ToastController,
        private loadingController: LoadingController
    ) { }

    public post(url: string, body: any, httpHeaders: any): Observable<any> {
            
            this.presentLoading('');
            return this.http.post<any>(url, body, httpHeaders)
                .pipe(
                tap(response => {
                    this.dismissLoading();
                }),
                catchError((error) => {
                    return this.handleError(error);
                })
                );
        
    }

    public put(url: string, body: any, httpHeaders: any): Observable<any> {

        this.presentLoading();
        return this.http.put<any>(url, body, httpHeaders).pipe(
            tap( response => {
                //console.log("response", response);
                 this.dismissLoading();
            }),
            catchError((error) => {
                return this.handleError(error);
            })
        );
    }

    public get(url: string, httpHeaders: any, isLoading: Boolean = true): Observable<any> {
        if(isLoading) {
            this.presentLoading('');
            return this.http.get<any>(url, httpHeaders).pipe(
                tap( response => {
                    setTimeout(() => {
                        this.dismissLoading(true);
                    }, 100);
                }),
                 catchError((error) => {
                    return this.handleError(error);
                })
            );
        }

        return this.http.get<any>(url, httpHeaders);
    }

    public delete(url: string, httpHeaders: any): Observable<any> {

        return this.http.delete<any>(url).pipe(
            tap( response => {
                 //console.log("response", response);
            }),
            catchError(this.handleError)
        );      
    }

    handleError = (error: HttpErrorResponse) => {
        this.dismissLoading();
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
          // client-side error
          errorMessage = `Error: ${error.error.message}`;
        } else if (error instanceof Array) {
            errorMessage = `Error: ${error.join("\n")}`;
            this.presentToast('top', errorMessage, 'danger');
            return;
        }
        else {
          // server-side error
          errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }

        return throwError(error);
    }

    async presentToast(position: 'top' | 'middle' | 'bottom', message, bgColor = 'success') {

        const toast = await this.toastController.create({
            message: message,
            duration: 1500,
            position: position,
            color: bgColor
        });

        await toast.present();
    }


    // Call when you want to show loading
    public async presentLoading(message = 'Loading...') {
        this.requestCount++;

        // If a loader already exists and is presented, just return
        if (this.loader) {
        // optional: update message if you want
        try {
            // update message safely if loader exists
            (this.loader as any).message = message;
        } catch (e) { /* ignore */ }
        return;
        }

        // create + present the loader
        this.loader = await this.loadingController.create({
        cssClass: 'spinner-only',
        message,
        spinner: 'crescent',

        });

        try {
        await this.loader.present();
        } catch (err) {
        // If present fails, clean up
        this.loader = null;
        this.requestCount = Math.max(0, this.requestCount - 1);
        console.warn('Loading present failed', err);
        }
    }

    // Call when you want to hide loading
    public async dismissLoading(force = false) {

        // decrement request count, but not below 0
        this.requestCount = Math.max(0, this.requestCount - 1);

        // Only dismiss if no remaining requests OR force requested
        if (this.requestCount > 0 && !force) {
        return;
        }

        // Reset counter to 0 when forced or actually dismissing
        this.requestCount = 0;

        // Try to dismiss the loader instance
        if (this.loader) {
        try {
            await this.loader.dismiss();
        } catch (err) {
            // dismiss can fail if overlay was already dismissed — ignore but log
            console.warn('Loader dismiss error (instance):', err);
        } finally {
            this.loader = null;
        }
        }

        // Fallback: ensure no leftover overlays in the controller stack
        await this.dismissAllOverlays();
    }

    // Extra safety: clear any remaining overlays (top-down)
    private async dismissAllOverlays() {
        try {
            // safety limit to prevent infinite loops
            const MAX_ITER = 20;
            let iter = 0;

            // getTop returns the top overlay element (or null)
            let top = await this.loadingController.getTop();

            while (top && iter < MAX_ITER) {
            iter++;

            try {
                // Preferred: call dismiss on the overlay element itself
                // (this dismisses that specific overlay reliably)
                if (typeof (top as any).dismiss === 'function') {
                await (top as any).dismiss();
                } else {
                // Fallback: try controller dismiss with id if available
                // Some Ionic versions expose overlay.id or overlay.overlayId
                const overlayId = (top as any).id ?? (top as any).overlayId ?? undefined;
                await this.loadingController.dismiss(undefined, undefined, overlayId);
                }
            } catch (err) {
                console.warn('Dismiss attempt failed for overlay:', err);
                // break to avoid infinite loop if dismiss repeatedly fails
                break;
            }

            // small yield (optional) to allow DOM/update cycles before re-check
            await new Promise(res => setTimeout(res, 0));

            top = await this.loadingController.getTop();
            }

            if (iter >= MAX_ITER) {
            console.warn('dismissAllOverlays reached MAX_ITER — something may still be stuck.');
            }
        } catch (err) {
            console.warn('Error while dismissing all overlays:', err);
        }
    }

}
